import { useCallback, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_SOUND_KEYS,
  type NotificationPreferenceGroup,
  type NotificationPreferences,
  type NotificationPreferencesUpdateInput,
  type NotificationSoundKey,
} from '@magimp/shared';
import { patchNotificationPreferences } from '../../../api/notifications.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { useNotificationSoundPreviewer } from '../hooks/useNotificationSoundPreviewer.js';
import { NOTIFICATION_PREFS_QK, useNotificationPreferences } from '../../notifications/index.js';

const GROUPS: ReadonlyArray<NotificationPreferenceGroup> = ['chat', 'rates', 'responsibleStatus'];

/**
 * @aiPurpose Settings → Notifications section (#1069). One panel,
 *   three group rows (chat / rates / responsibleStatus). Each row
 *   carries an enabled toggle, a sound select, and a Play preview
 *   button. Preferences are loaded from #1068's REST surface and
 *   auto-PATCHed on every change.
 * @aiInvariant per-user state — the route is `JwtAuthGuard`-only on
 *   the backend; this panel never asks for a permission. Stale sound
 *   keys are rejected at the wire by the shared `NotificationSoundKey`
 *   enum, so a future drop of an mp3 surfaces as a save error rather
 *   than silent persistence.
 * @aiSecurity preview audio playback is initiated ONLY from the
 *   `onClick` handler of the Play button so browser autoplay policy
 *   accepts the user gesture; the playback path swallows failures
 *   (toast surface) and never throws into React.
 */
export function NotificationSettingsPanel() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const { previewSound } = useNotificationSoundPreviewer();
  // WHY: latest-write-wins coalescing. Holds the most recent mutation
  //   id so an in-flight previous PATCH whose response loses the race
  //   does not clobber a more recent one (TanStack's onSuccess fires
  //   per-call, regardless of order).
  const latestMutationId = useRef(0);

  const query = useNotificationPreferences();

  const mutation = useMutation({
    mutationFn: (input: NotificationPreferencesUpdateInput) => patchNotificationPreferences(input),
    onSuccess: (next, _input, context) => {
      const id = context as number;
      if (id !== latestMutationId.current) return;
      queryClient.setQueryData<NotificationPreferences>(NOTIFICATION_PREFS_QK, next);
    },
    onError: (err) => {
      showToast({ tone: 'error', text: errorMessage(err) });
      // Re-read the canonical row so the UI shows what's actually stored.
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFS_QK });
    },
    onMutate: () => {
      latestMutationId.current += 1;
      return latestMutationId.current;
    },
  });

  const applyPatch = useCallback(
    (input: NotificationPreferencesUpdateInput) => {
      mutation.mutate(input);
    },
    [mutation],
  );

  if (query.isPending) {
    return <p style={{ margin: 0, color: 'var(--fg-2)' }}>{t('notifications.loading')}</p>;
  }
  if (query.isError) {
    return (
      <p style={{ margin: 0, color: 'var(--status-error-fg)' }}>
        {t('notifications.loadError')}: {errorMessage(query.error)}
      </p>
    );
  }

  const prefs = query.data;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {GROUPS.map((group) => (
        <NotificationGroupRow
          key={group}
          group={group}
          enabled={prefs[group].enabled}
          soundKey={prefs[group].soundKey}
          disabled={mutation.isPending}
          onChange={(patch) => applyPatch({ [group]: patch })}
          onPreview={previewSound}
        />
      ))}
    </div>
  );
}

interface NotificationGroupRowProps {
  group: NotificationPreferenceGroup;
  enabled: boolean;
  soundKey: NotificationSoundKey;
  disabled: boolean;
  onChange: (patch: { enabled?: boolean; soundKey?: NotificationSoundKey }) => void;
  onPreview: (soundKey: NotificationSoundKey) => void;
}

function NotificationGroupRow({
  group,
  enabled,
  soundKey,
  disabled,
  onChange,
  onPreview,
}: NotificationGroupRowProps) {
  const { t } = useTranslation('settings');
  const toggleId = useId();
  const selectId = useId();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, 200px) auto minmax(220px, 280px) auto',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <label htmlFor={toggleId} style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)' }}>
        {t(`notifications.groups.${group}`)}
      </label>
      <input
        id={toggleId}
        type="checkbox"
        role="switch"
        aria-label={t(`notifications.groups.${group}`)}
        checked={enabled}
        disabled={disabled}
        onChange={(e) => onChange({ enabled: e.target.checked })}
        data-testid={`notif-pref-${group}-enabled`}
      />
      <select
        id={selectId}
        aria-label={t('notifications.soundSelectLabel')}
        value={soundKey}
        disabled={disabled || !enabled}
        onChange={(e) => onChange({ soundKey: e.target.value as NotificationSoundKey })}
        data-testid={`notif-pref-${group}-sound`}
        style={{
          height: 32,
          borderRadius: 'var(--r-1)',
          border: '1px solid var(--border-1)',
          background: 'var(--bg-1)',
          color: 'var(--fg-1)',
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          padding: '0 8px',
        }}
      >
        {NOTIFICATION_SOUND_KEYS.map((key, idx) => (
          <option key={key} value={key}>
            {t('notifications.soundOption', { index: idx + 1 })}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onPreview(soundKey)}
        disabled={!enabled}
        aria-label={t('notifications.previewAriaLabel')}
        data-testid={`notif-pref-${group}-preview`}
        style={{
          height: 32,
          padding: '0 12px',
          borderRadius: 'var(--r-1)',
          border: '1px solid var(--border-1)',
          background: 'var(--bg-1)',
          color: 'var(--fg-1)',
          fontSize: 14,
          cursor: enabled ? 'pointer' : 'not-allowed',
          opacity: enabled ? 1 : 0.5,
        }}
      >
        {t('notifications.previewButton')}
      </button>
    </div>
  );
}
